import { jitsiLocalStorage } from '@jitsi/js-utils';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Icon, IconMenuThumb, IconMessage, IconContain, IconCover, IconModeratorPin, IconUserPinned, IconVolume, IconMicDisabled, IconCameraDisabled, IconCrown, IconMuteEveryoneElse, IconMuteVideoEveryoneElse, IconKick, IconRaisedHandSlash, IconUserGroup, IconEye, IconEyeOff, IconZoomIn, IconZoomOut } from '../../../base/icons';
import { setLocalZoomShareApproved, setModeratorZoomShareApproved } from '../../../zoom-sync/actions';
import { getLocalZoomShareApproved, getModeratorZoomShareApproved } from '../../../zoom-sync/functions';
import { getLocalParticipant, getParticipantById, muteRemoteParticipant, PARTICIPANT_ROLE, pinParticipant, SET_MODERATOR_PIN, isScreenShareParticipant, isLocalScreenshareParticipant, checkModeratorPinRateLimit } from '../../../base/participants';
import { MEDIA_TYPE } from '../../../base/media';
import { openDialog } from '../../../base/dialog';
import { KickRemoteParticipantDialog, MuteEveryoneDialog, MuteEveryonesVideoDialog, MuteRemoteParticipantDialog, MuteRemoteParticipantsVideoDialog, VolumeControlDialog, ForceMuteRemoteDialog, ForceMuteRemoteParticipantVideoDialog } from '../../../video-menu/components/web';
import { openGrantModeratorDialog } from '../../../video-menu/actions.web';
import { openChat, setIsPollsTabFocused } from '../../../chat/actions.web';
import { pollsTabFocusedHandler } from '../../../conference/actions.web';
import { closeParticipantsPaneModal, close } from '../../../participants-pane/actions';
import { getCurrentConference, setObjectFit } from '../../../base/conference';
import { setLargeVideoFullscreen } from '../../../large-video/actions.any';
import { NOTIFICATION_TYPE, showNotification } from '../../../notifications';
import { clearRaisedHandByParticipant } from '../../../participants-pane/functions';
import { isVisitorModeSupported } from '../../../visitors/functions';
import VideoMenuButton from '../../../video-menu/components/web/VideoMenuButton';
import { isParticipantAudioMuted, isParticipantVideoMuted } from '../../../base/tracks';
import { stateType } from '../../../app/stateTypes';
import DialogPortal from '../../../toolbox/components/web/DialogPortal';
import DemoteToVisitorDialog from '../../../video-menu/components/web/DemoteToVisitorDialog';
import { isDeviceLandScape } from '../../../base/environment/environment';
/**
 * React {@code Component} for displaying an icon associated with opening the
 * the {@code VideoMenu}.
 *
 * @augments {Component}
 */
function RemoteVideoMenu({ participantID }) {
    var _a;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [menuSize, setMenuSize] = useState({ width: 0, height: 0 });
    const [triggerRect, setTriggerRect] = useState(null);
    const isPrivateChatDisabled = useSelector((state) => { var _a; return (_a = state['features/chat']) === null || _a === void 0 ? void 0 : _a.isPrivateChatDisabled; });
    // Get all needed state from Redux
    const conference = useSelector(getCurrentConference);
    const participant = useSelector((state) => getParticipantById(state, participantID));
    const localParticipant = useSelector((state) => getLocalParticipant(state));
    const { objectFit, lockAudio, lockVideo } = useSelector((state) => state['features/base/conference']);
    const { remoteVideoMenu = {}, disableRemoteMute } = useSelector((state) => state['features/base/config']);
    const { largevideofullscreen } = useSelector((state) => state['features/large-video']);
    const isMainModerator = useSelector((state) => { var _a, _b, _c; return ((_b = (_a = state[stateType.base.jwt]) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.moderator) || ((_c = state[stateType.base.jwt]) === null || _c === void 0 ? void 0 : _c.moderator); });
    const isVisitorsModeSupported_flag = useSelector(isVisitorModeSupported);
    // Check moderator status
    const _isModerator = Boolean((localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.role) === PARTICIPANT_ROLE.MODERATOR);
    // Check if participant is a fake participant
    const isFakeParticipant = (_a = participant === null || participant === void 0 ? void 0 : participant.isFakeParticipant) !== null && _a !== void 0 ? _a : false;
    const isScreenShare = isScreenShareParticipant(participant);
    // Own screen share currently shown in the large video: offer hide/show of own screen.
    const isOwnScreenInLargeVideo = useSelector((state) => isLocalScreenshareParticipant(participant) && state['features/large-video'].participantId === participantID);
    const [showMyScreen, setShowMyScreen] = useState(() => jitsiLocalStorage.getItem('show_screen_while_screensharing') === 'true');
    const handleToggleMyScreen = useCallback(() => {
        const next = !showMyScreen;
        setShowMyScreen(next);
        jitsiLocalStorage.setItem('show_screen_while_screensharing', String(next));
        window.dispatchEvent(new CustomEvent('show-my-screen-changed', { detail: next }));
    }, [showMyScreen]);
    // Check if currently pinned
    const currentlyPinned = (participant === null || participant === void 0 ? void 0 : participant.pinned) || false;
    // Check mute states
    const isAudioMuted = useSelector((state) => isParticipantAudioMuted(participant, state));
    const isVideoMuted = useSelector((state) => isParticipantVideoMuted(participant, state));
    // Check moderator status
    const isTargetModerator = (participant === null || participant === void 0 ? void 0 : participant.role) === PARTICIPANT_ROLE.MODERATOR;
    // Check disableKick config
    const { disableKick } = remoteVideoMenu;
    // Handler functions
    const handlePrivateMessage = useCallback(() => {
        if (participant) {
            dispatch(openChat(participant));
            dispatch(closeParticipantsPaneModal());
            dispatch(close());
            dispatch(pollsTabFocusedHandler(false));
            dispatch(setIsPollsTabFocused(false));
        }
    }, [dispatch, participant]);
    const isModeratorZoomShareApproved = useSelector(getModeratorZoomShareApproved);
    const isLocalZoomShareApproved = useSelector(getLocalZoomShareApproved);
    const isZoomForEveryone = Boolean(isModeratorZoomShareApproved || isLocalZoomShareApproved);
    const handleToggleZoomShare = useCallback(() => {
        const nextValue = !isZoomForEveryone;
        dispatch(setModeratorZoomShareApproved(nextValue));
        dispatch(setLocalZoomShareApproved(nextValue));
    }, [dispatch, isZoomForEveryone]);
    const handleObjectFit = useCallback(() => {
        const currentObjectFit = objectFit || 'cover';
        dispatch(setObjectFit(currentObjectFit === 'cover' ? 'contain' : 'cover'));
    }, [dispatch, objectFit]);
    const handlePin = useCallback(() => {
        // Toggle pin state: if currently pinned, unpin (false), otherwise pin (true)
        dispatch(pinParticipant(participantID, !currentlyPinned));
    }, [dispatch, participantID, currentlyPinned]);
    const handleLargeVideoFullscreen = useCallback(() => {
        dispatch(pinParticipant(participantID, true));
        setTimeout(() => {
            dispatch(setLargeVideoFullscreen(!largevideofullscreen));
        }, 100);
    }, [dispatch, participantID, largevideofullscreen]);
    const participantModeratorPin = useCallback(() => {
        if (!conference) {
            return;
        }
        if (checkModeratorPinRateLimit(dispatch, t)) {
            return;
        }
        const myId = conference.myUserId();
        const isModeratorPinned = participant && participant.moderatorPin;
        const pinAction = isModeratorPinned ? 'unpin' : 'pin';
        conference.sendMessage({
            type: SET_MODERATOR_PIN,
            participant_id: participantID,
            senderId: myId,
            action: pinAction
        });
        // Show notification
        const notificationKey = pinAction === 'pin'
            ? t('videothumbnail.pinByModerator')
            : t('videothumbnail.unpinByModerator');
        // onLeave();
        dispatch(showNotification({
            appearance: NOTIFICATION_TYPE.NORMAL,
            titleKey: notificationKey,
            descriptionKey: t('videothumbnail.moderatorPinDiscription')
        }, 3000));
    }, [dispatch, participant, conference, participantID, t]);
    const handleVolumeControl = useCallback(() => {
        dispatch(openDialog(VolumeControlDialog, { participantID }));
    }, [dispatch, participantID]);
    const handleMuteAudio = useCallback(() => {
        if (isAudioMuted) {
            dispatch(muteRemoteParticipant(participantID, MEDIA_TYPE.AUDIO));
        }
        else {
            dispatch(openDialog(MuteRemoteParticipantDialog, { participantID }));
        }
    }, [dispatch, participantID, isAudioMuted]);
    const handleMuteVideo = useCallback(() => {
        if (isVideoMuted) {
            dispatch(muteRemoteParticipant(participantID, MEDIA_TYPE.VIDEO));
        }
        else {
            dispatch(openDialog(MuteRemoteParticipantsVideoDialog, { participantID }));
        }
    }, [dispatch, participantID, isVideoMuted]);
    const handleGrantModerator = useCallback(() => {
        dispatch(openGrantModeratorDialog(participantID));
    }, [dispatch, participantID]);
    const handleMuteEveryoneElse = useCallback(() => {
        dispatch(openDialog(MuteEveryoneDialog, { exclude: [participantID] }));
    }, [dispatch, participantID]);
    const handleMuteEveryoneElseVideo = useCallback(() => {
        dispatch(openDialog(MuteEveryonesVideoDialog, { exclude: [participantID] }));
    }, [dispatch, participantID]);
    const handleKick = useCallback(() => {
        dispatch(openDialog(KickRemoteParticipantDialog, { participantID }));
    }, [dispatch, participantID]);
    const handleForceMuteVideo = useCallback(() => {
        dispatch(openDialog(ForceMuteRemoteParticipantVideoDialog, { participantID }));
    }, [dispatch, participantID]);
    const handleForceMuteAudio = useCallback(() => {
        dispatch(openDialog(ForceMuteRemoteDialog, { participantID }));
    }, [dispatch, participantID]);
    const handleDemoteToVisitor = useCallback(() => {
        dispatch(openDialog(DemoteToVisitorDialog, { participantID }));
    }, [dispatch, participantID]);
    const handleClearRaisedHand = useCallback(() => {
        if (participant) {
            clearRaisedHandByParticipant(participant);
        }
    }, [participant]);
    const content = useMemo(() => (React.createElement("div", { className: 'remotevideomenu-child' },
        React.createElement(VideoMenuButton, { buttonText: t(currentlyPinned ? 'videothumbnail.unpinMe' : 'videothumbnail.pinMe'), icon: IconUserPinned, id: 'pin-unpin', onClick: handlePin }),
        _isModerator && (!(participant === null || participant === void 0 ? void 0 : participant.webinarHidden) || (participant === null || participant === void 0 ? void 0 : participant.moderatorPin)) && (React.createElement(VideoMenuButton, { buttonText: (participant === null || participant === void 0 ? void 0 : participant.moderatorPin) ? t('videothumbnail.unpinEveryone') : t('videothumbnail.pinEveryone'), icon: IconModeratorPin, id: 'moderator-pin', onClick: participantModeratorPin })),
        !isFakeParticipant && (React.createElement(VideoMenuButton, { buttonText: t('volumeSlider.label'), icon: IconVolume, id: 'volume-control', onClick: handleVolumeControl })),
        !isScreenShare && (React.createElement(VideoMenuButton, { buttonText: objectFit === 'cover' ? t('tooltip.switchToContain') : t('tooltip.switchToCover'), icon: objectFit === 'cover' ? IconContain : IconCover, id: 'fit-to-screen', onClick: handleObjectFit })),
        !isScreenShare && (React.createElement(VideoMenuButton, { buttonText: isZoomForEveryone
                ? t('dialog.zoomForMe', 'Zoom for Me')
                : t('dialog.zoomShareConfirm', 'Zoom for Everyone'), icon: isZoomForEveryone ? IconZoomOut : IconZoomIn, id: 'toggle-zoom-mode', onClick: handleToggleZoomShare })),
        isOwnScreenInLargeVideo && (React.createElement(VideoMenuButton, { buttonText: showMyScreen ? t('toolbar.hideMyScreen') : t('virtualBackground.showMyScreen'), icon: showMyScreen ? IconEyeOff : IconEye, id: 'toggle-my-screen', onClick: handleToggleMyScreen })),
        !(participant === null || participant === void 0 ? void 0 : participant.local) && (React.createElement(React.Fragment, null,
            !isFakeParticipant && !isPrivateChatDisabled && (React.createElement(VideoMenuButton, { buttonText: t('toolbar.privateMessage'), icon: IconMessage, id: `privmsglink_${participantID}`, onClick: handlePrivateMessage, privatemessage: true })),
            (window.innerWidth < 500 || isDeviceLandScape()) && (React.createElement(VideoMenuButton, { buttonText: largevideofullscreen ? t('videothumbnail.exitLargeVideoFullscreen') : t('videothumbnail.makeLargeVideoFullscreen'), icon: IconUserPinned, id: 'large-video-fullscreen', onClick: handleLargeVideoFullscreen })),
            _isModerator && !isFakeParticipant && (React.createElement(React.Fragment, null,
                !disableRemoteMute && !isAudioMuted && (React.createElement(VideoMenuButton, { buttonText: t(isAudioMuted ? 'videothumbnail.unmute' : 'videothumbnail.domute'), icon: IconMicDisabled, id: 'mute-audio', onClick: handleMuteAudio })),
                !disableRemoteMute && !isVideoMuted && (React.createElement(VideoMenuButton, { buttonText: t(isVideoMuted ? 'videothumbnail.unmuteVideo' : 'participantsPane.actions.stopVideo'), icon: IconCameraDisabled, id: 'mute-video', onClick: handleMuteVideo })),
                React.createElement(VideoMenuButton, { buttonText: (lockVideo === null || lockVideo === void 0 ? void 0 : lockVideo[participantID]) !== undefined && lockVideo[participantID] === true
                        ? t('participantsPane.actions.unForceMuteVideo')
                        : t('participantsPane.actions.forceMuteVideo'), icon: IconCameraDisabled, id: 'force-mute-video', onClick: handleForceMuteVideo }),
                React.createElement(VideoMenuButton, { buttonText: (lockAudio === null || lockAudio === void 0 ? void 0 : lockAudio[participantID]) !== undefined && lockAudio[participantID] === true
                        ? t('participantsPane.actions.unForceMute')
                        : t('participantsPane.actions.forceMute'), icon: IconMicDisabled, id: 'force-mute-audio', onClick: handleForceMuteAudio }),
                isMainModerator && (React.createElement(VideoMenuButton, { buttonText: t(isTargetModerator ? 'dialog.revokeModerationTitle' : 'videothumbnail.grantModerator'), icon: IconCrown, id: 'grant-moderator', onClick: handleGrantModerator })),
                React.createElement(VideoMenuButton, { buttonText: t('videothumbnail.domuteOthers'), icon: IconMuteEveryoneElse, id: 'mute-everyone-else', onClick: handleMuteEveryoneElse }),
                React.createElement(VideoMenuButton, { buttonText: t('videothumbnail.domuteVideoOfOthers'), icon: IconMuteVideoEveryoneElse, id: 'mute-everyone-else-video', onClick: handleMuteEveryoneElseVideo }),
                !disableKick && (React.createElement(VideoMenuButton, { buttonText: t('videothumbnail.kick'), displayClass: 'kicklink', icon: IconKick, id: 'kick', onClick: handleKick })),
                isVisitorsModeSupported_flag && (React.createElement(VideoMenuButton, { buttonText: t('videothumbnail.demote'), icon: IconUserGroup, id: 'demote-visitor', onClick: handleDemoteToVisitor })),
                (participant === null || participant === void 0 ? void 0 : participant.raisedHand) && (React.createElement(VideoMenuButton, { buttonText: t('participantsPane.actions.clearRaisedHand'), icon: IconRaisedHandSlash, id: 'clear-raised-hand', onClick: handleClearRaisedHand })))))))), [
        _isModerator,
        currentlyPinned,
        handleClearRaisedHand,
        disableKick,
        disableRemoteMute,
        handleGrantModerator,
        handleKick,
        handleMuteAudio,
        handleMuteEveryoneElse,
        handleMuteEveryoneElseVideo,
        handleMuteVideo,
        handleForceMuteAudio,
        handleForceMuteVideo,
        handleDemoteToVisitor,
        handleObjectFit,
        handlePin,
        isPrivateChatDisabled,
        handlePrivateMessage,
        handleVolumeControl,
        isAudioMuted,
        isFakeParticipant,
        isTargetModerator,
        isVideoMuted,
        objectFit,
        participant === null || participant === void 0 ? void 0 : participant.moderatorPin,
        participantID,
        participantModeratorPin,
        lockAudio,
        lockVideo,
        isVisitorsModeSupported_flag,
        isScreenShare,
        isOwnScreenInLargeVideo,
        showMyScreen,
        handleToggleMyScreen,
        largevideofullscreen,
        handleLargeVideoFullscreen,
        t
    ]);
    const updateTriggerRect = useCallback(() => {
        const el = triggerRef.current;
        if (el) {
            setTriggerRect(el.getBoundingClientRect());
        }
    }, []);
    const openMenu = useCallback(() => {
        updateTriggerRect();
        setIsOpen(true);
    }, [updateTriggerRect]);
    const closeMenu = useCallback(() => {
        setIsOpen(false);
    }, []);
    const toggleMenu = useCallback((e) => {
        var _a, _b;
        (_a = e === null || e === void 0 ? void 0 : e.stopPropagation) === null || _a === void 0 ? void 0 : _a.call(e);
        (_b = e === null || e === void 0 ? void 0 : e.preventDefault) === null || _b === void 0 ? void 0 : _b.call(e);
        if (isOpen) {
            closeMenu();
        }
        else {
            openMenu();
        }
    }, [closeMenu, isOpen, openMenu]);
    // Close on outside click/escape.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const onPointerDown = (e) => {
            const triggerEl = triggerRef.current;
            const menuEl = menuRef.current;
            const target = e.target;
            if (!(target instanceof Node)) {
                return;
            }
            if ((triggerEl === null || triggerEl === void 0 ? void 0 : triggerEl.contains(target)) || (menuEl === null || menuEl === void 0 ? void 0 : menuEl.contains(target))) {
                return;
            }
            closeMenu();
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                closeMenu();
            }
        };
        window.addEventListener('mousedown', onPointerDown, true);
        window.addEventListener('touchstart', onPointerDown, true);
        window.addEventListener('keydown', onKeyDown, true);
        return () => {
            window.removeEventListener('mousedown', onPointerDown, true);
            window.removeEventListener('touchstart', onPointerDown, true);
            window.removeEventListener('keydown', onKeyDown, true);
        };
    }, [closeMenu, isOpen]);
    // Keep anchored on scroll/resize while open.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const handler = () => updateTriggerRect();
        window.addEventListener('scroll', handler, true);
        window.addEventListener('resize', handler, true);
        return () => {
            window.removeEventListener('scroll', handler, true);
            window.removeEventListener('resize', handler, true);
        };
    }, [isOpen, updateTriggerRect]);
    const portalStyle = useMemo(() => {
        if (!triggerRect) {
            return {
                display: 'none'
            };
        }
        // Position the menu to the left of the trigger, bottom-aligned.
        const GAP_PX = 8;
        const VIEWPORT_PADDING = 8;
        const width = menuSize.width || 0;
        const height = menuSize.height || 0;
        let left = triggerRect.left - width - GAP_PX;
        let top = triggerRect.bottom - height;
        // Clamp within viewport (position: fixed coordinates).
        const maxLeft = window.innerWidth - width - VIEWPORT_PADDING;
        const maxTop = window.innerHeight - height - VIEWPORT_PADDING;
        left = Math.max(VIEWPORT_PADDING, Math.min(left, maxLeft));
        top = Math.max(VIEWPORT_PADDING, Math.min(top, maxTop));
        return {
            position: 'fixed',
            left: `${left}px`,
            top: `${top}px`,
            zIndex: 1000,
            pointerEvents: 'auto'
        };
    }, [menuSize.height, menuSize.width, triggerRect]);
    return (React.createElement("span", { className: 'custom-participnt-dialog-parent', "data-ignore-pin": true },
        React.createElement("span", { className: 'custom-participnt-parent', onClick: toggleMenu, ref: triggerRef, title: 'Remote user controls' },
            React.createElement(Icon, { size: '20', src: IconMenuThumb })),
        isOpen && (React.createElement(DialogPortal, { className: 'custom-participnt-dialog-parent remote-video-menu-portal', getRef: () => { }, onVisible: () => { }, setSize: ({ width, height }) => setMenuSize({ width, height }), style: portalStyle, targetSelector: '.custom-participnt-dialog' },
            React.createElement("div", { className: 'custom-participnt-dialog', style: {
                    display: 'flex',
                    position: 'static',
                    right: 'auto',
                    bottom: 'auto'
                }, onClick: e => e.stopPropagation(), ref: menuRef }, content)))));
}
export default RemoteVideoMenu;
